import Layout from "@/components/shared/Layout";
import Hero from "@/components/home/Hero";
import AboutUs from "@/components/home/AboutUs";
import Programs from "@/components/home/Programs";
import Head from "next/head";
import Partners from "@/components/shared/Partners";
import SeoTagsSlider from "@/components/shared/SeoTagsSlider";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { META_IMAGE, SITE_URL } from "@/utils/meta";
import Statistics from "@/components/home/Statistics";
import OurAdvantages from "@/components/home/OurAdvantages";
import ScrollAnimation from "@/components/shared/ScrollAnimation";
import StudentsFeedbacks from "@/components/home/StudentsFeedbacks";
import JobGuaranteeSection from "@/components/home/JobGuaranteeSection";
import FAQ from "@/components/home/FAQ";
import ScholarshipCard from "@/components/shared/ScholarshipCard";
import WhatsappButton from "@/components/shared/WhatsappButton";
import ScrollToTopButton from "@/components/shared/ScrollToTopButton";
import CertificatePopup from "@/components/shared/Popup";

export async function getServerSideProps({ locale }) {
  let seoTags = [];

  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/tags/`, {
      headers: { "ngrok-skip-browser-warning": "true" },
    });
    if (res.ok) {
      const data = await res.json();
      seoTags = data.map((tag) => ({
        label: `#${tag.name}`,
        slug: tag.related_seo_page_slug,
      }));
    }
  } catch {
  }

  return {
    props: {
      ...(await serverSideTranslations(locale || "az")),
      seoTags,
    },
  };
}

export default function Home({ seoTags }) {
  const { t, i18n } = useTranslation();
  const pageUrl = `${SITE_URL}${i18n.language === "az" ? "" : `/${i18n.language}`}`;

  const scrollToSection = (sectionId, position) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: position,
      });
    }
  };
  return (
    <div>
      <Head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>{t("meta-title")}</title>
        <meta name="keywords" content="peerstack, peerstack academy, peerstack tech, proqramlaşdırma kursları, IT kursları Bakı, front-end kurs, back-end kurs, full-stack kurs, software engineering, coding bootcamp Azerbaijan, proqramçılıq təhsili" />
        <meta name="description" content={t("meta-description")} />
        <meta name="image" content={META_IMAGE} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:site_name" content="peerstack.tech" />
        <meta property="og:title" content={t("meta-title")} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:description" content={t("meta-description")} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={META_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta-title")} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:description" content={t("meta-description")} />
        <meta name="twitter:image" content={META_IMAGE} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "PeerStack",
              alternateName: "PeerStack Tech",
              url: "https://peerstack.tech",
              logo: "https://peerstack.tech/images/logo.png",
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "50 999 88 77",
                contactType: "customer service",
                areaServed: "AZ",
                availableLanguage: "Azerbaijani",
              },
              sameAs: [
                "https://peerstack.tech",
                "https://www.instagram.com/peerstack.tech",
                "https://www.facebook.com/peerstack.tech",
                "https://www.linkedin.com/company/peerstack-academy/",
              ],
            }),
          }}
        />
      </Head>
      <Layout scrollToSection={scrollToSection}>
        <Hero />
        <ScrollAnimation delay={0}>
          <AboutUs />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <ScholarshipCard />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <Statistics />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <OurAdvantages />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <Programs />
        </ScrollAnimation>
        <ScrollAnimation delay={100} className="overflow-hidden">
          <StudentsFeedbacks />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <JobGuaranteeSection />
        </ScrollAnimation>
        <ScrollAnimation delay={100}>
          <FAQ />
        </ScrollAnimation>
        <ScrollAnimation delay={100} className="overflow-hidden">
          <SeoTagsSlider tags={seoTags} />
        </ScrollAnimation>
        <ScrollAnimation delay={100} className="overflow-hidden">
          <Partners />
        </ScrollAnimation>
        <ScrollToTopButton />
        <WhatsappButton />
        <CertificatePopup />
      </Layout>
    </div>
  );
}
